import React, { useState, useEffect, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaEnvelope,
  FaLock,
  FaArrowLeft,
  FaUserShield,
  FaCheckCircle,
} from "react-icons/fa";
import { API_BASE_URL } from "../environment";
import { API } from "../common/Constants";
import { AuthContext } from "../context/AuthContext";
import { analyticsService } from "../services/analyticsService";
import "../styles/verification.css";

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const verifiedEmail = location.state?.verifiedEmail || "";
  const justVerified = location.state?.justVerified || false;
  const passwordReset = location.state?.passwordReset || false;
  const { login } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    email: verifiedEmail,
    password: "",
    rememberMe: false,
  });

  const [formErrors, setFormErrors] = useState({});
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    submitted: false,
    error: false,
    justVerified: justVerified,
    passwordReset: passwordReset,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error for this field when user types
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
    }

    if (!formData.password) {
      errors.password = "Password is required";
    }

    return errors;
  };

  // Check if user was just redirected from verification or password reset
  useEffect(() => {
    if (justVerified) {
      // If user was just verified, show a message or highlight field
      setFormStatus((prev) => ({ ...prev, justVerified: true }));
    }
    if (passwordReset) {
      // If user just reset their password, show a message
      setFormStatus((prev) => ({ ...prev, passwordReset: true }));
    }
  }, [justVerified, passwordReset]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormStatus((prev) => ({
      ...prev,
      submitting: true,
      submitted: false,
      error: false,
    }));

    try {
      // Use the local login endpoint
      const response = await fetch(`${API_BASE_URL}${API.LOGIN}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: formData.email,
          password: formData.password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || "Login failed");
      }

      const data = await response.json();

      // Track successful login event
      analyticsService.trackEvent(
        'User',
        'Login',
        `Success`,
        1
      );

      // Use AuthContext to login
      login({
        userId: data.userId,
        token: data.token,
        email: formData.email,
        fullName: data.fullName || "",
      });

      setFormStatus((prev) => ({
        ...prev,
        submitting: false,
        submitted: true,
        error: false,
      }));

      // Navigate to dashboard after a short delay
      setTimeout(() => {
        navigate("/dashboard");
      }, 1500);
    } catch (error) {
      console.error("Login error:", error);
      setFormStatus((prev) => ({
        ...prev,
        submitting: false,
        error: true,
        errorMessage:
          error.message || "Invalid login credentials. Please try again.",
      }));
    }
  };

  return (
    <section className="login-page section-padding">
      <div className="container">
        <div className="section-header">
          <h2>Welcome Back</h2>
          <p>Login to access your Pathshala Sanchalak dashboard</p>
        </div>

        <div className="login-content">
          <div className="login-form-container">
            <Link to="/" className="back-link">
              <FaArrowLeft /> Back to Home
            </Link>

            {formStatus.submitted ? (
              <div className="success-message">
                <h3>Login Successful!</h3>
                <p>
                  You are now logged in. You will be redirected to your
                  dashboard shortly.
                </p>
              </div>
            ) : (
              <form className="login-form" onSubmit={handleSubmit}>
                {formStatus.justVerified && (
                  <div className="verification-success-alert">
                    <FaCheckCircle />
                    <p>
                      Your email has been verified! You can now log in to your
                      account.
                    </p>
                  </div>
                )}

                {formStatus.passwordReset && (
                  <div className="verification-success-alert">
                    <FaCheckCircle />
                    <p>
                      Your password has been reset successfully! Please log in
                      with your new password.
                    </p>
                  </div>
                )}

                {formStatus.error && (
                  <div className="error-alert">
                    <p>
                      {formStatus.errorMessage ||
                        "Login failed. Please try again."}
                    </p>
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="email">
                    <FaEnvelope /> Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={formErrors.email ? "error" : ""}
                  />
                  {formErrors.email && (
                    <span className="error-message">{formErrors.email}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="password">
                    <FaLock /> Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={formErrors.password ? "error" : ""}
                  />
                  {formErrors.password && (
                    <span className="error-message">{formErrors.password}</span>
                  )}
                </div>

                <div className="form-options">
                  <div className="checkbox-group">
                    <div className="checkbox-container">
                      <input
                        type="checkbox"
                        id="rememberMe"
                        name="rememberMe"
                        checked={formData.rememberMe}
                        onChange={handleChange}
                      />
                      <label htmlFor="rememberMe">Remember Me</label>
                    </div>
                  </div>

                  <div className="forgot-password">
                    <Link to="/forgot-password" className="text-link">
                      Forgot Password?
                    </Link>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary login-btn"
                  disabled={formStatus.submitting}
                >
                  {formStatus.submitting ? "Logging in..." : "Login"}
                </button>

                <div className="register-link">
                  Don't have an account?{" "}
                  <Link to="/register" className="text-link">
                    Register Now
                  </Link>
                </div>
              </form>
            )}
          </div>

          <div className="login-info">
            <div className="login-info-content">
              <h3>Secure Access</h3>
              <div className="login-info-icon">
                <FaUserShield />
              </div>
              <p className="login-info-description">
                Access your administrative dashboard to manage your educational
                institution, track student progress, and oversee faculty
                activities.
              </p>
              <ul className="login-benefits">
                <li>
                  <span className="benefit-dot"></span>
                  Complete student and faculty management
                </li>
                <li>
                  <span className="benefit-dot"></span>
                  Attendance and performance analytics
                </li>
                <li>
                  <span className="benefit-dot"></span>
                  Fee management and financial reporting
                </li>
                <li>
                  <span className="benefit-dot"></span>
                  Scheduling and resource allocation
                </li>
              </ul>
              <div className="login-info-footer">
                <p>Need assistance with login?</p>
                <Link to="/#contact" className="support-link">
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Login;
